/**
 * 职责：顶层容器，连接 Source → store → Timeline 渲染。
 * 链路位置：业务页面 <AgentSession source={...} /> 唯一入口。
 * 当前裁剪范围：单会话；initialEvents 支持 EventBuffer 快照续流（M3）；reducer/transport 异常自吞。
 */
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { createSessionStore } from '../core/store';
import { replayEvents } from '../core/replay';
import {
  SessionStoreContext,
  NodeRegistryContext,
  ToolDisplayOptionsContext,
  SessionViewOptionsContext,
} from '../core/context/SessionContext';
import type { Source } from '../transport/types';
import { SseTransportTerminalError } from '../transport/createAgentxSseSource';
import type { LangchainAgentEvent, TierOverrides } from '../types/events';
import type { ReducerError } from '../types/tree';
import { createDefaultRegistry } from './defaultRegistry';
import type { NodeRegistry } from './NodeRegistry';
import { AgentLoopView } from './agentLoop/AgentLoopView';
import { InteractionBusContext } from '../core/context/InteractionContext';
import type { InteractionBus } from '../core/interaction/InteractionBus';
import { getNoopInteractionBus } from '../core/interaction/InteractionBus';
import { useInternalErrors, useSessionStatus } from '../core/hooks';
import type { MarkdownRenderer } from '../core/context/MarkdownRendererContext';
import { MarkdownRendererContext } from '../core/context/MarkdownRendererContext';
import {
  createDefaultToolRegistry,
  type ToolDisplayRegistry,
} from './tools/registry';
import type { ToolBodyMode } from './tools/types';
import type { PermissionUiMode, DisplayMode } from './sessionViewOptions';
import { DEFAULT_SESSION_VIEW_OPTIONS } from './sessionViewOptions';

export interface AgentSessionProps {
  source: Source;
  /** 挂载时先 replay 预灌树（含 EventBuffer ingest 快照），再接 source 续流 */
  initialEvents?: LangchainAgentEvent[];
  registry?: NodeRegistry;
  tierOverrides?: TierOverrides;
  onError?: (error: Error) => void;
  autoReconnect?: boolean;
  debug?: boolean;
  /** 为 true 且主舞台节点数超过阈值时使用 VirtualTimeline（V2-E） */
  virtualized?: boolean;
  virtualizeThreshold?: number;
  /** 权限回写薄层；PermissionCard 触发 onPermissionResolve（V2-D） */
  interactionBus?: InteractionBus;
  /** 按工具名注册 Body 组件（V3-T0） */
  toolDisplayRegistry?: ToolDisplayRegistry;
  /** 工具正文默认 preview / full（V3-T0） */
  defaultBodyMode?: ToolBodyMode;
  /** @deprecated Phase 4 起由 projection GroupedToolUseAccumulator 自动分组；保留 prop 兼容 */
  groupParallelTools?: boolean;
  /** 权限 UI：standalone | inline | both（V3-T4.6）；后台 bypass 时通常无 pending */
  permissionUiMode?: PermissionUiMode;
  /** 富 Markdown 渲染（simple demo 可注入 react-markdown + mermaid） */
  markdownRenderer?: MarkdownRenderer;
  /** 对齐 CLI display mode（normal / brief_only / transcript） */
  displayMode?: DisplayMode;
  /** 对齐 CC verbose；为 true 时跳过分组并展开 explore detail */
  verbose?: boolean;
  exploreFullscreenBash?: boolean;
  memoryDir?: string | null;
  workspaceRoot?: string | null;
  children?: ReactNode;
}

function DebugPanel() {
  const status = useSessionStatus();
  const errors = useInternalErrors();
  if (errors.length === 0) return null;
  return (
    <div className="lax-debug-panel" data-testid="lax-debug-panel">
      <div>status: {status}</div>
      <ul>
        {errors.map((err: ReducerError, i: number) => (
          <li key={`${err.eventIndex}-${i}`}>
            [{err.eventType}] {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AgentSession({
  source,
  initialEvents,
  registry,
  tierOverrides: _tierOverrides,
  onError,
  autoReconnect: _autoReconnect,
  debug = false,
  virtualized = false,
  virtualizeThreshold,
  interactionBus,
  toolDisplayRegistry,
  defaultBodyMode = 'preview',
  groupParallelTools = false,
  permissionUiMode = 'standalone',
  markdownRenderer,
  displayMode = 'normal',
  verbose = false,
  exploreFullscreenBash = true,
  memoryDir = null,
  workspaceRoot = null,
  children,
}: AgentSessionProps) {
  const storeRef = useRef<ReturnType<typeof createSessionStore> | null>(null);
  if (storeRef.current === null) {
    const replayOpts = {
      tierOverrides: _tierOverrides,
      collectDebug: debug,
    };
    const initialTree =
      initialEvents && initialEvents.length > 0
        ? replayEvents(initialEvents, replayOpts)
        : undefined;
    storeRef.current = createSessionStore(initialTree, {
      tierOverrides: _tierOverrides,
      debug,
      initialEventCount: initialEvents?.length ?? 0,
    });
  }
  const registryRef = useMemo(() => registry ?? createDefaultRegistry(), [registry]);
  const busRef = useMemo(
    () => interactionBus ?? getNoopInteractionBus(),
    [interactionBus],
  );
  const toolDisplayRef = useMemo(
    () => ({
      registry: toolDisplayRegistry ?? createDefaultToolRegistry(),
      defaultBodyMode,
    }),
    [toolDisplayRegistry, defaultBodyMode],
  );
  const sessionViewRef = useMemo(
    () => ({
      ...DEFAULT_SESSION_VIEW_OPTIONS,
      groupParallelTools,
      permissionUiMode,
      verboseReasoning: debug,
      displayMode,
      verbose,
      exploreFullscreenBash,
      memoryDir,
      workspaceRoot,
    }),
    [
      groupParallelTools,
      permissionUiMode,
      debug,
      displayMode,
      verbose,
      exploreFullscreenBash,
      memoryDir,
      workspaceRoot,
    ],
  );

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const store = storeRef.current!;
    const controller = new AbortController();

    void source.start((event, ctx) => {
      store.getState().applyEvent(event, ctx);
    }, controller.signal).catch((err: unknown) => {
      if (err instanceof SseTransportTerminalError) {
        const { tree } = store.getState();
        if (tree.status === 'running' || tree.status === 'connecting') {
          store.setState({ tree: { ...tree, status: 'error' } });
        }
        onErrorRef.current?.(err);
      } else if (err instanceof Error) {
        onErrorRef.current?.(err);
      }
    });

    return () => {
      controller.abort();
    };
  }, [source]);

  return (
    <SessionStoreContext.Provider value={storeRef.current!}>
      <InteractionBusContext.Provider value={busRef}>
        <NodeRegistryContext.Provider value={registryRef}>
          <MarkdownRendererContext.Provider value={markdownRenderer}>
            <ToolDisplayOptionsContext.Provider value={toolDisplayRef}>
              <SessionViewOptionsContext.Provider value={sessionViewRef}>
                <div className="lax-agent-session" data-testid="lax-agent-session">
                  {children ?? (
                    <AgentLoopView
                      virtualized={virtualized}
                      virtualizeThreshold={virtualizeThreshold}
                      groupParallelTools={groupParallelTools}
                    />
                  )}
                  {debug ? <DebugPanel /> : null}
                </div>
              </SessionViewOptionsContext.Provider>
            </ToolDisplayOptionsContext.Provider>
          </MarkdownRendererContext.Provider>
        </NodeRegistryContext.Provider>
      </InteractionBusContext.Provider>
    </SessionStoreContext.Provider>
  );
}
